import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classnames from "classnames";
import MediaQuery from "react-responsive";
import * as breakpoints from "../../data/breakpoints";
import * as userActions from "../../actions/userActions";
import * as favouriteActions from "../../actions/favouriteActions";
import * as reportActions from "../../actions/reportActions";
import * as followActions from "../../actions/followActions";
import * as accessActions from "../../actions/accessActions";
import * as organisationActions from "../../actions/organisationActions";
import { folderActionsAccess } from "../../utils/accessLevelCheck";
import PageContainer from "../Global/Containers/PageContainer";
import MaxWidthContainer from "../Global/Containers/MaxWidthContainer";
import MainContentContainer from "../Global/Containers/MainContentContainer";
import DrillContent from "./DrillContent";
import Comments from "../Global/Comments/Comments";
import DrillActions from "./DrillActions";
import RelatedContent from "./RelatedContent";
import Advert from "../Global/Adverts/Advert";
import AddToFolderModal from "../Folders/AddToFoldersModal";
import EditContentLinks from "../Global/Links/EditContentLinks";
import UpsellModal from "../Global/Modals/UpsellModal";
import { TCM_SQUARE_AD, NX_SQUARE_AD, TCM_PORTRAIT_AD } from "../../data/adverts";

class DrillPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      foldersModalOpen: false,
      upsellModalOpen: false,
      upsellModalFeature: `folders`,
    };
  }

  componentWillReceiveProps(nextProps) {
    // loggedInUser API is fired as part of App.js as it is used by the <Header> component
    // This means that the loggedInUser and content API's are asynchronous
    // Which is why we need these checks so that we can fire only once no matter on what order the API's return
    if (this.props.content.id !== nextProps.content.id) {
      this.callLoggedInOnlyAPIs(nextProps);
    }

    if (this.props.loggedInUser !== nextProps.loggedInUser) {
      this.callLoggedInOnlyAPIs(nextProps);
    }
  }

  userLoggedInAndContentReturned = (nextProps) => {
    if (nextProps.loggedInUser.id && nextProps.content.id) {
      return true;
    }
    return false;
  };

  callLoggedInOnlyAPIs = (nextProps) => {
    if (this.userLoggedInAndContentReturned(nextProps)) {
      this.props.actions.isFavourite(
        nextProps.content.id,
        nextProps.content.type,
      );
      this.props.actions.doIFollow([nextProps.content.author.id]);
    }
  };

  printClick = () => {
    // If we're printing a diagram we want to print in landscape, not portrait
    // The following isn't possible with CSS as we're altering @media & @page
    // NOTE: This doesn't work in Safari https://caniuse.com/#feat=css-paged-media
    if (this.props.content.type === `pitchdiagram`) {
      const css = `@media print { @page { size: A4 landscape; } };`;
      const head = document.head || document.getElementsByTagName(`head`)[0];
      const style = document.createElement(`style`);
      style.type = `text/css`;
      if (style.styleSheet) {
        style.styleSheet.cssText = css;
      } else {
        style.appendChild(document.createTextNode(css));
      }
      head.appendChild(style);
    }

    window.print();
  };

  handlefavouriteClick = (e) => {
    e.preventDefault();
    if (this.props.loggedInUser.computed_access) {
      // Logged in users
      this.togglefavourite();
    } else {
      this.showUpsellModal(`favourite`);
    }
  };

  togglefavourite = () => {
    if (this.props.favourited === true) {
      this.props.actions.removeFavourite(
        this.props.content.id,
        this.props.content.type,
      );
    } else {
      this.props.actions.addFavourite(
        this.props.content.id,
        this.props.content.type,
      );
    }
  };

  reportClick = () => {
    this.props.actions.addReport(
      this.props.content.id,
      this.props.content.type,
    );
  };

  handleFollowClick = (action) => {
    const authorId = this.props.content.author.id;
    if (action === `follow`) {
      this.props.actions.followUser(authorId);
    } else {
      this.props.actions.unfollowUser(authorId);
    }
  };

  openFoldersModal = () => {
    this.setState({ foldersModalOpen: true });
  };

  closeModal = (modal) => {
    this.setState({ [modal]: false });
  };

  handleExportImageClick = () => {
    // https://stackoverflow.com/a/21210576/1134548
    const { snapshot_url, title } = this.props.content;
    const tempLink = document.createElement(`a`);
    tempLink.href = snapshot_url;
    tempLink.download = `${title}.jpg`;
    document.body.appendChild(tempLink);
    tempLink.click();
    setTimeout(() => {
      tempLink.parentNode.removeChild(tempLink);
    }, 10);
  };

  handleAddToFolderClick = (e) => {
    e.preventDefault();
    if (this.props.loggedInUser.computed_access) {
      const canAccess = folderActionsAccess(
        this.props.loggedInUser.computed_access,
      );
      if (canAccess === false) {
        // Logged in but don't have access to the feature
        e.preventDefault();
        this.showUpsellModal(`folders`);
      } else {
        this.openFoldersModal();
      }
    } else {
      this.showUpsellModal(`folders`);
    }
  };

  showUpsellModal = (upsellType) => {
    this.setState({ upsellModalOpen: true, upsellModalFeature: upsellType });
  };

  render() {
    const {
      loggedInUser,
      content,
      favourited,
      commentCount,
      doIFollow,
      access,
      organisation,
    } = this.props;
    const {
      foldersModalOpen,
      upsellModalOpen,
      upsellModalFeature,
    } = this.state;

    const pageClass = classnames(`bg-white drill-page`, {
      "content-locked": access.canIAccess === false,
      "cms-created": content.cms_created === true,
      "diagram-page": content.type === `pitchdiagram`,
    });

    let commentContentType;
    if (content.type === `pitchdiagram`) {
      commentContentType = `pitchdiagram`;
    } else {
      commentContentType = `content`;
    }

    const drillActions = (
      <DrillActions
        cmsCreated={content.cms_created}
        printClick={this.printClick}
        favourited={favourited}
        favouriteClick={this.handlefavouriteClick}
        reported={content.reported}
        reportClick={this.reportClick}
        folderClick={this.handleAddToFolderClick}
        loggedInUser={loggedInUser}
        exportImageClick={this.handleExportImageClick}
        contentType={content.type}
      />
    );

    return (
      <PageContainer classes={pageClass}>
        {content.author && (
          <EditContentLinks
            userId={loggedInUser.id}
            authorId={content.author.id}
            contentId={content.id}
            contentType={content.type}
            cmsCreated={content.cms_created}
          />
        )}

        <MaxWidthContainer>
          <MainContentContainer>
            <div className="row">
              <div className="col-lg-8 col-main-content">
                <DrillContent
                  content={content}
                  access={access}
                  organisation={organisation}
                  commentCount={commentCount}
                  doIFollow={doIFollow}
                  onFollowClick={this.handleFollowClick}
                  drillActions={drillActions}
                  loggedInUser={loggedInUser}
                />

                {(loggedInUser.notLoggedIn || loggedInUser.display_adverts) && (
                  <MediaQuery maxWidth={breakpoints.LARGE}>
                    <div className="advert-wrap border-radius-l bg-grey--lightest border-1 border-color-grey--light">
                      <Advert advert={TCM_SQUARE_AD} />
                      <Advert advert={NX_SQUARE_AD} />
                    </div>
                  </MediaQuery>
                )}

                <Comments
                  contentId={content.id}
                  contentType={commentContentType}
                  loggedInUser={loggedInUser}
                />
              </div>

              <div className="col-lg-4 col-right-sidebar">
                <div className="right-sidebar">
                  {content.related_content && (
                    <RelatedContent content={content.related_content} />
                  )}

                  {(loggedInUser.notLoggedIn ||
                    loggedInUser.display_adverts) && (
                    <MediaQuery minWidth={breakpoints.LARGE}>
                      <div className="advert-wrap border-radius-l bg-grey--lightest border-1 border-color-grey--light">
                        <Advert advert={TCM_PORTRAIT_AD} />
                        <Advert advert={NX_SQUARE_AD} />
                      </div>
                    </MediaQuery>
                  )}
                </div>
              </div>
            </div>
          </MainContentContainer>
        </MaxWidthContainer>

        <AddToFolderModal
          contentId={content.id}
          contentType={content.type}
          modalIsOpen={foldersModalOpen}
          closeModal={() => this.closeModal(`foldersModalOpen`)}
        />

        <UpsellModal
          loggedInUser={loggedInUser}
          modalIsOpen={upsellModalOpen}
          upsellFeature={upsellModalFeature}
          onCloseClick={() => this.closeModal(`upsellModalOpen`)}
        />
      </PageContainer>
    );
  }
}

DrillPage.propTypes = {
  loggedInUser: PropTypes.object.isRequired,
  content: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  favourited: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  commentCount: PropTypes.number,
  doIFollow: PropTypes.array,
  access: PropTypes.object,
  organisation: PropTypes.object,
};

function mapStateToProps(state) {
  let content = state.content;
  if (state.diagram.id) {
    content = state.diagram;
  }

  return {
    loggedInUser: state.loggedInUser,
    content,
    favourited: state.favourited,
    commentCount: state.comments.count,
    doIFollow: state.doIFollow,
    access: state.access,
    organisation: state.organisation,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      Object.assign(
        {},
        userActions,
        favouriteActions,
        reportActions,
        followActions,
        accessActions,
        organisationActions,
      ),
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DrillPage);



// WEBPACK FOOTER //
// ./library/app/components/Drill/DrillPage.js